import { z } from 'zod';

/**
 * Agregados diários globais do super-admin (Fase A da Sprint 4 — BI
 * Completo). Um documento por dia em `/aggregations/daily/{yyyymmdd}`.
 *
 * `schemaVersion: 2` é o esquema final com Fase A obrigatória + Fase B
 * opcional (`engagement`, `distribution`, `labels.generatedToday`).
 * Esta sub-sprint (4.1, Fase A) populará apenas os blocos obrigatórios;
 * os opcionais ganham produção em 4.6.
 */

const NonNegInt = z.number().int().nonnegative();
const Cents = NonNegInt;
const Pct = z.number().min(0).max(1);
const YyyymmddRegex = /^\d{8}$/;
const YyyymmRegex = /^\d{6}$/;

export const DailyAggregateSchema = z.object({
  yyyymmdd: z.string().regex(YyyymmddRegex),
  tz: z.literal('America/Sao_Paulo'),
  schemaVersion: z.literal(2),

  tenants: z.object({
    active: NonNegInt,
    suspended: NonNegInt,
    total: NonNegInt,
    createdToday: NonNegInt,
    suspendedToday: NonNegInt,
    reactivatedToday: NonNegInt,
  }),

  members: z.object({
    createdToday: NonNegInt,
    total: NonNegInt,
  }),

  mrr: z.object({
    byCurrency: z.object({
      BRL: Cents,
      USD: Cents,
      EUR: Cents,
    }),
    usdEquivalentCents: Cents,
    arptUsdCents: Cents,
  }),

  netNewMrr: z.object({
    newUsdCents: Cents,
    churnedUsdCents: Cents,
    netUsdCents: z.number().int(),
  }),

  labels: z.object({
    printedToday: NonNegInt.nullable(),
    generatedToday: NonNegInt.nullable(),
  }),

  /**
   * #743 (F8 da #679) — repasse mensal esperado dos PARCEIROS, fora do MRR
   * (`isDirectPaying` exclui parceiro): o que os tenants `PARTNER` ativos
   * repassam, pelo equivalente mensal (anual ÷ 12, como a D15). É o "card
   * próprio de repasse" do dashboard financeiro. Optional: dias anteriores à
   * F8 não o têm, e o card mostra "sem dado" em vez de zero.
   */
  partnerRemit: z
    .object({
      byCurrency: z.object({ BRL: Cents, USD: Cents, EUR: Cents }),
      activeTenants: NonNegInt,
    })
    .optional(),

  /**
   * #753 (A4 do Asaas) — comissão de PARCEIRO DIRECT gerada pelos pagamentos
   * do dia (a métrica `partner_split_commission` da issue), por moeda e por
   * forma de pagamento: `split` (Asaas pagou), `connect` (Stripe Connect, A8)
   * e `payable` (a pagar por fora). É a parte do bruto do MRR que não é nossa
   * — o MRR continua pelo bruto (decisão da #748). Optional: dias anteriores à
   * A4 não o têm, e o card mostra "sem dado" em vez de zero.
   */
  partnerSplitCommission: z
    .object({
      byCurrency: z.object({
        BRL: z.object({ split: Cents, connect: Cents, payable: Cents }),
        USD: z.object({ split: Cents, connect: Cents, payable: Cents }),
        EUR: z.object({ split: Cents, connect: Cents, payable: Cents }),
      }),
      payments: NonNegInt,
    })
    .optional(),

  engagement: z
    .object({
      dau: NonNegInt.nullable(),
      wau: NonNegInt.nullable(),
      mau: NonNegInt.nullable(),
      stickinessPct: Pct.nullable(),
    })
    .optional(),

  distribution: z
    .object({
      tenantsByLabelBucket: z.record(
        z.enum(['B0_10', 'B11_50', 'B51_200', 'B201_1000', 'B1000_PLUS']),
        NonNegInt,
      ),
      tenantsByCountry: z.record(z.string(), NonNegInt),
      tenantsByCurrency: z.record(z.string(), NonNegInt),
    })
    .optional(),

  fxRates: z.object({
    BRL: z.number().positive(),
    USD: z.literal(1),
    EUR: z.number().positive(),
  }),

  /**
   * #795 (2026-10-23) — `true` quando algum `paymentSettlement` do dia estava
   * ilegível (falhou no `PaymentSettlementSchema`) e ficou FORA de
   * `partnerSplitCommission`: a comissão do dia está SUBCONTADA. Antes o job só
   * logava e o número saía como se fosse completo. Optional: dias anteriores
   * não o têm (equivale a `false`). Quem exibe a comissão deve marcar o dia.
   */
  commissionPartial: z.boolean().optional(),
  /** #795 — quantos settlements ilegíveis ficaram de fora (acompanha `commissionPartial`). */
  commissionUnreadableCount: NonNegInt.optional(),
  /**
   * #795 (2026-10-23) — instante (ms) da última RECOMPOSIÇÃO dos campos de
   * EVENTO do dia (vendas, cadastros, suspensões, etiquetas, comissão) sobre um
   * doc já existente. Presente = o job rodou de novo sobre este dia e, de
   * propósito, NÃO regravou os campos de SNAPSHOT (`tenants.active/suspended/
   * total`, `members.total`, `mrr.*`, `partnerRemit`), que continuam como na
   * primeira computação (`computedAt`). Ver `storeDailyAggregate`.
   */
  eventsRecomputedAt: NonNegInt.optional(),

  computedAt: NonNegInt,
  computedBy: z.enum(['aggregateDailyMetrics', 'backfill']),
});
export type DailyAggregate = z.infer<typeof DailyAggregateSchema>;

export const MetricKeySchema = z.enum([
  'mrrUsd',
  'mrrBrl',
  'mrrUsdc',
  'mrrEur',
  'arpt',
  'sales',
  'users',
  'labels',
  'netNewMrr',
  'churn',
  'totalActive',
  'dau',
  'wau',
  'mau',
  'cohortRetention',
  'funnelStep',
]);
export type MetricKey = z.infer<typeof MetricKeySchema>;

export const YYYYMMDD_REGEX = YyyymmddRegex;
export const YYYYMM_REGEX = YyyymmRegex;

// =============== Cohort retention (Fase B) ===============
const RetentionWindow = z.object({
  d1: Pct,
  d7: Pct,
  d14: Pct,
  d30: Pct,
  d60: Pct,
  d90: Pct,
});

/**
 * Gravado em `aggregations/cohorts/months/{yyyymm}` — que compartilha o
 * collection group `months` com `tenants/{tenantId}/billing/usage/months`.
 *
 * Consequência: **`labelsPrinted` é nome RESERVADO aqui.** O total all-time do
 * `/dashboard/usage` soma `sum('labelsPrinted')` sobre esse CG; hoje o doc de
 * cohort não tem o campo e contribui 0. Adicioná-lo infla o número global em
 * silêncio. Há tripwire em `usagePaths.test.ts`. Ver docs/label-counters.md.
 */
export const CohortRetentionSchema = z.object({
  cohortMonth: z.string().regex(YyyymmRegex),
  schemaVersion: z.literal(1),
  cohortSize: NonNegInt,
  retention: RetentionWindow,
  cumulativeChurn: RetentionWindow,
  computedAt: NonNegInt,
  computedBy: z.enum(['aggregateCohortRetention', 'backfill']),
});
export type CohortRetention = z.infer<typeof CohortRetentionSchema>;

// =============== Funnel diário (Fase B) ===============
export const FunnelStepSchema = z.enum([
  'PAGEVIEW_LANDING',
  'SIGNUP_STARTED',
  'OTP_SENT',
  'OTP_VERIFIED',
  'EMAIL_VERIFIED',
  'TENANT_CREATED',
  'BILLING_ACTIVE',
  'FIRST_LABEL_PRINTED',
  'D7_ACTIVE',
]);
export type FunnelStep = z.infer<typeof FunnelStepSchema>;
export const ALL_FUNNEL_STEPS = FunnelStepSchema.options;

export const DailyFunnelSchema = z.object({
  yyyymmdd: z.string().regex(YyyymmddRegex),
  schemaVersion: z.literal(1),
  steps: z.record(FunnelStepSchema, NonNegInt),
  cohortFromYyyymm: z.string().regex(YyyymmRegex),
  computedAt: NonNegInt,
  computedBy: z.enum(['aggregateFunnel', 'backfill']),
});
export type DailyFunnel = z.infer<typeof DailyFunnelSchema>;

// =============== Health (Fase B) ===============
export const HealthSignalKindSchema = z.enum([
  'PRINT_ERROR_RECURRING',
  'SUSPECTED_DATA_RESET',
  'BILLING_INCONSISTENT',
  'ZOMBIE',
  'PRE_CHURN_RISK',
  'BACKFILL_PENDING',
]);
export type HealthSignalKind = z.infer<typeof HealthSignalKindSchema>;
export const ALL_HEALTH_SIGNAL_KINDS = HealthSignalKindSchema.options;

export const TenantHealthSnapshotSchema = z.object({
  tenantId: z.string(),
  signals: z.array(HealthSignalKindSchema),
  primarySignal: HealthSignalKindSchema,
  // B-20: limita `details` a primitivos serializáveis pelo Firestore. Era
  // `z.unknown()`, aceitava classes/funções/BigInt/Date que travam o write.
  details: z.record(
    z.string(),
    z.union([z.string(), z.number(), z.boolean(), z.null()]),
  ),
});
export type TenantHealthSnapshot = z.infer<typeof TenantHealthSnapshotSchema>;

export const DailyHealthSchema = z.object({
  yyyymmdd: z.string().regex(YyyymmddRegex),
  schemaVersion: z.literal(1),
  countsBySignal: z.record(HealthSignalKindSchema, NonNegInt),
  /**
   * Tenants com `status === ACTIVE` avaliados neste dia — o DENOMINADOR das
   * proporções calculadas sobre `countsBySignal`, e a razão de ele morar aqui
   * e não no `DailyAggregate` (#620).
   *
   * O `ZOMBIE_RATIO_HIGH` dividia `countsBySignal.ZOMBIE` por
   * `tenants.active` do agregado diário, que conta outra coisa: assinaturas
   * `ACTIVE` COM id no gateway. Populações diferentes, e o resultado passava
   * de 1 — em produção o alerta gravou `magnitude: 1.5` e o texto "150% dos
   * tenants ativos estão como ZOMBIE", todo dia, por quatro meses.
   *
   * Vindo do mesmo job que conta os signals, sobre a mesma lista já filtrada
   * de `DELETED`, o invariante `ZOMBIE <= activeTenants` passa a valer por
   * construção em vez de por coincidência.
   *
   * Opcional porque os documentos históricos não o têm, e torná-lo
   * obrigatório faria o `safeParse` de `detectAnomalies` devolver `null` para
   * todos eles — calando de uma vez o `ZOMBIE_RATIO_HIGH` E o
   * `SUSPECTED_DATA_RESET_SPIKE`. Trocaríamos um alarme que mente por dois que
   * sumiram. Quem consome trata `undefined` como "não dá para calcular".
   */
  activeTenants: NonNegInt.optional(),
  topProblematic: z.array(TenantHealthSnapshotSchema).max(50),
  /**
   * #795 (2026-10-23) — `true` quando o snapshot do dia NÃO é confiável: o
   * `aggregateHealth` detectou entrada implausível (ex.: contadores de uso do
   * período vieram vazios com muitos tenants ACTIVE, o que transformaria a base
   * inteira em ZOMBIE/PRE_CHURN_RISK). O doc é gravado mesmo assim, com os
   * `countsBySignal` de ZOMBIE/PRE_CHURN_RISK zerados e este marcador, para o
   * dia não ficar sem registro nem mentir. Quem consome (`detectAnomalies`,
   * dashboard de saúde) deve tratar `degraded === true` como "sem dado" para as
   * regras de zumbi. Optional: docs anteriores não o têm (equivale a `false`).
   */
  degraded: z.boolean().optional(),
  /** #795 — código curto do motivo de `degraded` (ex.: `usage_counters_empty`). */
  degradedReason: z.string().optional(),
  computedAt: NonNegInt,
  computedBy: z.enum(['aggregateHealth', 'backfill']),
});
export type DailyHealth = z.infer<typeof DailyHealthSchema>;

// =============== PostHog engagement (Fase B) ===============
export const DailyPostHogSchema = z.object({
  yyyymmdd: z.string().regex(YyyymmddRegex),
  schemaVersion: z.literal(1),
  dau: NonNegInt.nullable(),
  wau: NonNegInt.nullable(),
  mau: NonNegInt.nullable(),
  stickinessPct: Pct.nullable(),
  computedAt: NonNegInt,
  computedBy: z.enum(['aggregatePostHog', 'backfill']),
});
export type DailyPostHog = z.infer<typeof DailyPostHogSchema>;

// =============== Operação (Sprint 4.8 — impressão) ===============
// Agregados de impressão do dia, vindos dos eventos PostHog do Android e do
// iOS (web não imprime). Android: `print_executed` por etiqueta (protocol/
// sizeMm), `print_failed` (protocol/reason), `printer_connected` (model).
// iOS (#939): `label_printed_bulk` somando `count`, `print_failed`
// (transport/errorCode), `printer_connected` (printerModel) — os nomes das
// duas plataformas são unidos no `GROUP_EXPR` do `aggregateOperation`. As
// chaves de protocolo trazem o protocolo do Android (`TSPL`…) e o transporte
// do iOS (`BLE_GATT`); `printsBySize` é só Android.
export const DailyOperationSchema = z.object({
  yyyymmdd: z.string().regex(YyyymmddRegex),
  schemaVersion: z.literal(1),
  // print_executed agrupado.
  printsByProtocol: z.record(z.string(), NonNegInt),
  printsBySize: z.record(z.string(), NonNegInt),
  // print_failed agrupado.
  failuresByProtocol: z.record(z.string(), NonNegInt),
  failuresByReason: z.record(z.string(), NonNegInt),
  // printer_connected agrupado por modelo (nome BT). Opcional para
  // compatibilidade com docs gravados antes da instrumentação de conexão.
  connectionsByModel: z.record(z.string(), NonNegInt).optional(),
  // Totais derivados; null = Query API falhou (UI mostra "—").
  totalPrints: NonNegInt.nullable(),
  totalFailures: NonNegInt.nullable(),
  totalConnections: NonNegInt.nullable().optional(),
  computedAt: NonNegInt,
  computedBy: z.enum(['aggregateOperation', 'backfill']),
});
export type DailyOperation = z.infer<typeof DailyOperationSchema>;

// =============== Alerts (Fase B) ===============
export const AlertKindSchema = z.enum([
  'MRR_DROP',
  'SALES_DROP',
  'NET_NEW_NEGATIVE',
  'CHURN_SPIKE',
  'SUSPECTED_DATA_RESET_SPIKE',
  'PRINT_ERROR_SPIKE',
  'ZOMBIE_RATIO_HIGH',
  // #267: superfície que parou de mandar evento ao PostHog. O canal de
  // observabilidade ficou morto meses sem ninguém notar — silêncio é
  // indistinguível de calmaria até alguém perguntar todo dia.
  'TELEMETRY_SURFACE_SILENT',
  // #295: evento que DEVERIA ter chegado ao PostHog e não chegou, quando o
  // rastro no Firestore prova que o fato de negócio aconteceu. Sem esse
  // cruzamento, "zero eventos" é indistinguível de "ninguém executou o fluxo"
  // — foi assim que o step OTP_VERIFIED do funil mediu zero por sprints lendo
  // um evento que nenhum código emitia.
  'EXPECTED_EVENT_SILENT',
]);
export type AlertKind = z.infer<typeof AlertKindSchema>;
export const ALL_ALERT_KINDS = AlertKindSchema.options;

export const AlertSeveritySchema = z.enum(['LOW', 'MEDIUM', 'HIGH']);
export type AlertSeverity = z.infer<typeof AlertSeveritySchema>;
export const ALL_ALERT_SEVERITIES = AlertSeveritySchema.options;

export const AlertSchema = z.object({
  alertId: z.string(),
  kind: AlertKindSchema,
  severity: AlertSeveritySchema,
  magnitude: z.number(),
  observedAt: NonNegInt,
  periodAffected: z.object({
    fromYyyymmdd: z.string().regex(YyyymmddRegex),
    toYyyymmdd: z.string().regex(YyyymmddRegex),
  }),
  detail: z.string(),
  acknowledgedBy: z.string().nullable(),
  acknowledgedAt: NonNegInt.nullable(),
  /**
   * Por que o alerta foi reconhecido, quando quem reconheceu foi um SCRIPT.
   *
   * As rules limitam o super-admin a escrever `['acknowledgedBy',
   * 'acknowledgedAt']`, então a UI do admin nunca preenche este campo e só o
   * Admin SDK consegue. A distinção fica estrutural, não por convenção de
   * nome: REASON PRESENTE = carimbo de backfill, REASON AUSENTE = uma pessoa
   * olhou e decidiu.
   *
   * Existe porque o contrário apaga informação: depois de um backfill
   * reconhecer 125 alertas de uma vez, quem abrir a coleção daqui a três meses
   * veria 125 alertas "reconhecidos" e concluiria que foram revisados por
   * alguém. Não foram — e a justificativa estaria numa issue talvez já
   * fechada. Pedido da sessão MONITOR na #620, e a razão dela é a que importa:
   * um write não pode ficar indistinguível de um julgamento.
   */
  acknowledgedReason: z.string().nullable().optional(),
  /**
   * Instante em que a TTL do Firestore pode apagar este alerta.
   *
   * É um `Timestamp` do Firestore, nunca epoch-ms: a política de TTL IGNORA
   * campos que não sejam Timestamp, e um número criaria uma política que
   * existe, parece funcionar e nunca apaga nada. Tipado como `z.unknown()`
   * pelo mesmo motivo que `labels.ttlAt` em `schemas.ts` — o cliente nunca o
   * interpreta, só o servidor escreve.
   *
   * Não se chama `ttlAt` de propósito: a política vale para o par
   * (collection group, campo), e `items` é um nome compartilhado com
   * `audit/billing/items` e cinco coleções `config/<alvo>/items`. Um `ttlAt`
   * aqui armaria o
   * gatilho de deleção sobre a trilha de auditoria de billing.
   *
   * Ausente nos `HIGH`, e isso é decisão, não esquecimento: o relatório mensal
   * do admin lê TODOS os alertas HIGH sem recorte de data, então expirá-los
   * esvaziaria retroativamente a seção de alertas de relatórios antigos.
   */
  biAlertTtlAt: z.unknown().nullable().optional(),
  emailSent: z.boolean(),
});
export type Alert = z.infer<typeof AlertSchema>;

// =============== Backfill manual (recompute de agregações) ===============
// As agregações que dependem do PostHog (`aggregatePostHog`, `aggregateFunnel`,
// `aggregateOperation`) NÃO gravam o dia quando a Query API falha (desde
// 2026-10-23, #795; docs anteriores podem ter `null`) — o dia fica sem doc. O
// callable `backfillAggregations` (super-admin) recomputa um intervalo de dias
// BRT, regravando os docs com `computedBy: 'backfill'`. `daily` e `health` NÃO
// entram aqui: são fotos do estado "agora" e não se reconstroem (#795).
export const BackfillAggregationTypeSchema = z.enum([
  'posthog',
  'funnel',
  'operation',
]);
export type BackfillAggregationType = z.infer<
  typeof BackfillAggregationTypeSchema
>;
export const ALL_BACKFILL_AGGREGATION_TYPES =
  BackfillAggregationTypeSchema.options;

export const BackfillAggregationsInput = z
  .object({
    type: BackfillAggregationTypeSchema,
    fromYyyymmdd: z.string().regex(YyyymmddRegex),
    toYyyymmdd: z.string().regex(YyyymmddRegex),
  })
  .refine((v) => v.fromYyyymmdd <= v.toYyyymmdd, {
    message: 'fromYyyymmdd deve ser <= toYyyymmdd',
    path: ['toYyyymmdd'],
  });
export type BackfillAggregationsInput = z.infer<typeof BackfillAggregationsInput>;

export const BackfillAggregationsOutputSchema = z.object({
  type: BackfillAggregationTypeSchema,
  fromYyyymmdd: z.string().regex(YyyymmddRegex),
  toYyyymmdd: z.string().regex(YyyymmddRegex),
  successCount: NonNegInt,
  failureCount: NonNegInt,
  failedDates: z.array(z.string().regex(YyyymmddRegex)),
});
export type BackfillAggregationsOutput = z.infer<
  typeof BackfillAggregationsOutputSchema
>;
